import React from 'react';
import { AppBar, AppBarSection, AppBarSpacer } from '@progress/kendo-react-layout';
import Logo from '../../assets/images/Logo.svg?react';
import { GlobalSearchFilter } from '../grid/toolbar/globalFilters/GlobalSearchFilter';
import SettingsButton from './SettingsButton';
import { RoutePath } from '../../global/enums/RoutePath';
import { GearSvgIcon } from '../GearSvgIcon';
import { NotificationBellButton } from './notificationCenter/NotificationBellButton';
import { IndexDropdown } from '../grid/toolbar/IndexDropdown';
import { useGearDialog } from '../common/useGearDialog';
import { useViewsContext } from '../../context/view/ViewsContext';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useGlobalizationContext } from '../../context/globalization/GlobalizationContext';
import { useTranslation } from '../../context/globalization/useTranslation';
import { clientConfig } from '../../config/clientConfig';

interface TopBarProps {
  maintainancePagePreview?: boolean;
}

const TopBar: React.FC<TopBarProps> = props => {
  const { pathname } = useLocation();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const isSettingsPath = matchPath(`${RoutePath.SETTINGS}/*`, pathname);
  const isHomePath = matchPath(`${RoutePath.HOME}`, pathname);
  const isAccessDeniedPath = matchPath(`${RoutePath.ACCESS_DENIED}`, pathname);
  const isMaintenancePath = matchPath(`${RoutePath.MAINTENANCE}`, pathname);
  const navigate = useNavigate();

  const { hasUnsavedChanges, setHasUnsavedChanges } = useViewsContext();
  const { showDialog, DialogComponent } = useGearDialog();

  const handleBackToHome = () => {
    if (hasUnsavedChanges) {
      showDialog({
        title: translate('custom.settings.views.private.unsavedChangesTitle'),
        message: translate('custom.settings.views.private.unsavedChangesMessage'),
        confirmButtonLabel: translate('custom.common.discard'),
        discardButtonLabel: translate('custom.common.cancel'),
        onConfirm: confirmed => {
          if (confirmed) {
            setHasUnsavedChanges?.(false);
            navigate(RoutePath.HOME);
          }
        }
      });
    } else {
      navigate(RoutePath.HOME);
    }
  };

  return (
    <AppBar className="topbar">
      {isSettingsPath && !props.maintainancePagePreview ? (
        <>
          <AppBarSection>
            <GearSvgIcon customIcon="backPageIcon" className="settingsBackHome" onClick={handleBackToHome} />
          </AppBarSection>
          <AppBarSection className="settingsTitle">
            <h1>{translate('custom.settings.title')}</h1>
          </AppBarSection>
        </>
      ) : (
        <>
          <AppBarSection>
            <Logo
              className="gearLogo"
              onClick={() => (!props.maintainancePagePreview ? window.location.reload() : undefined)}
            />
            {clientConfig.environment && clientConfig.environment !== 'PROD' && (
              <span className="environmentLabel">{clientConfig.environment}</span>
            )}
          </AppBarSection>
          {isHomePath && (
            <>
              <AppBarSpacer />
              <AppBarSection>
                <GlobalSearchFilter />
              </AppBarSection>
              <AppBarSpacer />
            </>
          )}
        </>
      )}
      {!isAccessDeniedPath && !isMaintenancePath && !props.maintainancePagePreview && (
        <>
          <AppBarSection className="topBarRightSection">
            <IndexDropdown />
            <div className="topBarActionButtons">
              <NotificationBellButton />
              <SettingsButton />
            </div>
          </AppBarSection>
        </>
      )}
      {DialogComponent}
    </AppBar>
  );
};

export default TopBar;
